/**
 * Rodapé de ações ao arrastar card — estilo Kommo
 */

import React from 'react';
import { CheckCircle2, Trash2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DropZoneAction } from '../hooks/useCardDropActions';
import { PipelineType } from '../types';

export interface DragFooterZone {
  id: DropZoneAction;
  label: string;
  accent: string;
  hoverBg: string;
  icon?: React.ReactNode;
}

interface PipelineDragFooterProps {
  zones: DragFooterZone[];
  activeZone: DropZoneAction | null;
  onDragOver: (zone: DropZoneAction) => void;
  onDragLeave: () => void;
  onDrop: (zone: DropZoneAction) => void;
}

const QUALIFICACAO_ZONES: DragFooterZone[] = [
  {
    id: 'excluir',
    label: 'Excluir',
    accent: 'border-red-500',
    hoverBg: 'bg-red-50 dark:bg-red-950/30',
    icon: <Trash2 className="w-5 h-5 text-red-500" />,
  },
  {
    id: 'perdido',
    label: 'Perdido',
    accent: 'border-gray-400',
    hoverBg: 'bg-gray-100 dark:bg-zinc-800',
  },
  {
    id: 'venda',
    label: 'Venda',
    accent: 'border-green-500',
    hoverBg: 'bg-green-50 dark:bg-green-950/30',
  },
];

const POS_VENDA_ZONES: DragFooterZone[] = [
  {
    id: 'fixado',
    label: 'Fixado',
    accent: 'border-green-500',
    hoverBg: 'bg-green-50 dark:bg-green-950/30',
    icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
  },
  {
    id: 'perdido',
    label: 'Perdido',
    accent: 'border-gray-400',
    hoverBg: 'bg-gray-100 dark:bg-zinc-800',
    icon: <XCircle className="w-5 h-5 text-gray-500" />,
  },
];

const VENDA_ZONES: DragFooterZone[] = [
  {
    id: 'excluir',
    label: 'Excluir',
    accent: 'border-red-500',
    hoverBg: 'bg-red-50 dark:bg-red-950/30',
    icon: <Trash2 className="w-5 h-5 text-red-500" />,
  },
  {
    id: 'perdido',
    label: 'Perdido',
    accent: 'border-gray-400',
    hoverBg: 'bg-gray-100 dark:bg-zinc-800',
  },
];

export function getDragFooterZones(tipo?: PipelineType | string | null): DragFooterZone[] {
  if (tipo === 'pos_venda') return POS_VENDA_ZONES;
  if (tipo === 'venda') return VENDA_ZONES;
  return QUALIFICACAO_ZONES;
}

export function PipelineDragFooter({
  zones,
  activeZone,
  onDragOver,
  onDragLeave,
  onDrop,
}: PipelineDragFooterProps) {
  if (zones.length === 0) return null;

  return (
    <div className="flex-shrink-0 border-t border-gray-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm">
      <div className="flex items-stretch justify-center max-w-2xl mx-auto h-14">
        {zones.map((zone) => (
          <div
            key={zone.id}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              onDragOver(zone.id);
            }}
            onDragLeave={onDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(zone.id);
            }}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 border-t-2 transition-colors cursor-copy',
              zone.accent,
              activeZone === zone.id ? zone.hoverBg : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50'
            )}
          >
            {zone.icon ?? (
              <span className="text-[11px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-widest">
                {zone.label}
              </span>
            )}
            {zone.icon && (
              <span className="text-[9px] text-gray-400 font-medium">{zone.label}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default PipelineDragFooter;
